import type { AuditHandoffAction } from '@compass/client/types/audit';
import React from 'react';

import type { FieldGroup } from '@/types/formSchema';
import { handoffFieldLabel } from '@/utils/handoffField';

interface HandoffNotesPanelProps {
  handoffActions: AuditHandoffAction[];
  /** Output schema groups, used to show each action's field LABEL rather than its raw id. */
  outputGroups: FieldGroup[];
}

/**
 * Surfaces the handoff actions a human auditor left for the caller when routing the
 * case to a human call. Rendered at the top of the Human Call tab's left pane so the
 * caller sees what to follow up on before dialing.
 *
 * HIPAA: notes may be PHI — display only, never logged or persisted client-side.
 */
const HandoffNotesPanelComponent: React.FC<HandoffNotesPanelProps> = ({
  handoffActions,
  outputGroups,
}) => {
  // Resolve raw short field names to schema labels once, not per render per row —
  // handoffFieldLabel walks every output leaf.
  const labels = React.useMemo(
    () => handoffActions.map(action => handoffFieldLabel(outputGroups, action.field)),
    [handoffActions, outputGroups],
  );

  if (handoffActions.length === 0) return null;

  return (
    <div className="shrink-0 border-b border-muted/60 px-4 pb-3 pt-4">
      <h2 className="mb-2 text-sm font-semibold text-foreground">Handoff from auditor</h2>
      <ul className="space-y-2">
        {handoffActions.map((action, index) => (
          <li key={`${action.field ?? ''}-${index}`} className="flex flex-col gap-0.5">
            {labels[index] && (
              <span className="break-words text-[11px] font-semibold text-foreground">
                {labels[index]}
              </span>
            )}
            {action.note && (
              <span className="whitespace-pre-wrap text-[12.5px] text-muted-foreground">
                {action.note}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const HandoffNotesPanel = React.memo(HandoffNotesPanelComponent);
HandoffNotesPanel.displayName = 'HandoffNotesPanel';
